/**
 * DocumentUploadModal — collect document metadata (+ optional file) and submit.
 * In mock mode the binary is ignored; in live mode the http service sends it.
 * Mirrors the modal pattern of CustomerModal/UserModal.
 */

import { useState, useEffect, useMemo } from 'react'
import { X, Upload, FileText, AlertCircle, RefreshCw } from 'lucide-react'
import type {
  UploadDocumentInput,
  DocumentCategory,
  DocumentEntityType,
  Document,
  DocumentWorkspaceService,
} from '@/types/document.types'
import {
  DOCUMENT_CATEGORY_OPTIONS,
  labelForCategory,
  ASSESSMENT_YEAR_OPTIONS,
} from '@/types/document.types'
import { resolveRole } from '@/security/accessContract'
import { useAuth } from '@/contexts/AuthContext'
import { getAssessmentService } from '@/services/assessments'
import { returnCasesHttp } from '@/services/returnCases'
import { useNavigate } from 'react-router-dom'
import { canAccessRoute } from '@/security/uiAccess'
import { useCustomerStore } from '@/store/customerStore'

interface DocumentUploadModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (input: UploadDocumentInput) => Promise<void>
  /** When provided, the upload is pre-linked to this entity (e.g. a customer). */
  linkedEntityType?: DocumentEntityType
  linkedEntityId?: string
  /** When replacing an existing document (creates new version). */
  replaceDocument?: Document | null
}

export default function DocumentUploadModal(props: DocumentUploadModalProps) {
  const { user } = useAuth()
  const authority = JSON.stringify([
    user?.id,
    user?.currentSessionId,
    user?.authScope,
    user?.tenantId,
    user?.role,
    [...(user?.permissions ?? [])].sort(),
  ])
  return <UploadWorkspace key={authority} {...props} />
}

function UploadWorkspace({
  isOpen,
  onClose,
  onSubmit,
  linkedEntityType,
  linkedEntityId,
  replaceDocument,
}: DocumentUploadModalProps) {
  const [destination, setDestination] = useState<'workspace' | 'vault' | ''>('')
  const [name, setName] = useState('')
  const [category, setCategory] = useState<DocumentCategory>('other')
  const [assessmentYear, setAssessmentYear] = useState('')
  const [financialYear, setFinancialYear] = useState('')
  const [workspaceService, setWorkspaceService] = useState<DocumentWorkspaceService | ''>('')
  const [description, setDescription] = useState('')
  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const navigate = useNavigate()
  const { user, hasPermission } = useAuth()
  const isAssistant = ['technical_assistant', 'research_assistant'].includes(
    resolveRole(user?.role).canonicalRole ?? ''
  )
  const canReadCases = !isAssistant && hasPermission('txh.assessment.case.read')
  const canReadLegacy = !isAssistant && hasPermission('txh.assessment.read')
  const canCreateCase = !isAssistant && hasPermission('txh.assessment.case.create')
  const [assessmentLink, setAssessmentLink] = useState('')
  const [assessments, setAssessments] = useState<
    Array<{ id: string; type: string; label: string; year: string }>
  >([])
  const [assessmentError, setAssessmentError] = useState('')
  const [assessmentsLoading, setAssessmentsLoading] = useState(false)
  const customerId = linkedEntityType === 'customer' ? linkedEntityId : selectedCustomerId
  const firmDocument = !customerId && (!linkedEntityType || linkedEntityType === 'ca_firm')
  const categoryOptions = useMemo<DocumentCategory[]>(
    () =>
      firmDocument
        ? [
            'gst_registration_certificate',
            'partnership_deed',
            'moa_aoa',
            'audit_report',
            'balance_sheet',
            'profit_loss_statement',
            'bank_statement',
            'rent_agreement',
            'other',
          ]
        : DOCUMENT_CATEGORY_OPTIONS,
    [firmDocument]
  )
  const [file, setFile] = useState<File | undefined>(undefined)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const customers = useCustomerStore(s => s.customers)
  const loadCustomers = useCustomerStore(s => s.loadCustomers)

  useEffect(() => {
    setDestination(replaceDocument ? 'workspace' : '')
    if (replaceDocument) {
      setName(replaceDocument.name)
      setCategory(replaceDocument.category)
      setAssessmentYear(replaceDocument.assessmentYear || '')
      setFinancialYear(replaceDocument.financialYear || '')
      setWorkspaceService(replaceDocument.workspaceService || '')
      setDescription(replaceDocument.description || '')
    } else {
      setName('')
      setCategory('other')
      setAssessmentYear('')
      setFinancialYear('')
      setWorkspaceService('')
      setDescription('')
      setSelectedCustomerId('')
      setAssessmentLink('')
      setFile(undefined)
      setError('')
    }
  }, [replaceDocument, isOpen])

  useEffect(() => {
    if (isOpen && destination === 'workspace' && !linkedEntityId && customers.length === 0) {
      loadCustomers()
    }
  }, [isOpen, destination, linkedEntityId, customers.length, loadCustomers])

  useEffect(() => {
    if (!isOpen || destination !== 'workspace' || !customerId || replaceDocument) return
    let cancelled = false
    setAssessments([])
    setAssessmentLink('')
    setAssessmentError('')
    setAssessmentsLoading(true)
    const operations = [
      canReadCases
        ? returnCasesHttp.list({ customerId, pageSize: 100 }).then(result =>
            result.items.map(item => ({
              id: item.id,
              type: 'assessment_case',
              label: `${item.period_value} · ${item.return_kind} #${item.return_sequence} · ${item.id.slice(0, 8)}`,
              year: item.period_value,
            }))
          )
        : Promise.resolve([]),
      canReadLegacy
        ? getAssessmentService()
            .listForCustomer(customerId)
            .then(result =>
              result.map(item => ({
                id: item.id,
                type: 'tax_assessment',
                label: `Legacy · ${item.assessmentYear} · ${item.itrType} · ${item.id.slice(0, 8)}`,
                year: item.assessmentYear,
              }))
            )
        : Promise.resolve([]),
    ]
    Promise.allSettled(operations).then(results => {
      if (cancelled) return
      setAssessments(results.flatMap(result => (result.status === 'fulfilled' ? result.value : [])))
      if (results.some(result => result.status === 'rejected'))
        setAssessmentError('Some assessments could not be loaded. Reopen the upload form to retry.')
      setAssessmentsLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [isOpen, destination, customerId, replaceDocument, canReadCases, canReadLegacy])

  useEffect(() => {
    if (!categoryOptions.includes(category)) setCategory('other')
  }, [categoryOptions, category])

  if (!isOpen) return null

  const reset = () => {
    setName('')
    setCategory('other')
    setAssessmentYear('')
    setFinancialYear('')
    setWorkspaceService('')
    setDescription('')
    setSelectedCustomerId('')
    setAssessmentLink('')
    setFile(undefined)
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (destination !== 'workspace') return
    const resolvedName = name.trim() || file?.name?.trim() || ''
    if (!resolvedName) {
      setError('Provide a document name or choose a file')
      return
    }
    const selectedAssessment = assessments.find(
      item => `${item.type}:${item.id}` === assessmentLink
    )
    const resolvedEntityType =
      replaceDocument?.linkedEntityType ||
      selectedAssessment?.type ||
      linkedEntityType ||
      (selectedCustomerId ? 'customer' : 'ca_firm')
    const resolvedEntityId =
      replaceDocument?.linkedEntityId ||
      selectedAssessment?.id ||
      linkedEntityId ||
      selectedCustomerId ||
      undefined
    const caseLinked = resolvedEntityType === 'assessment_case'
    const resolvedService = caseLinked ? 'income_tax' : workspaceService
    if (resolvedEntityType !== 'ca_firm' && !resolvedService) {
      setError('Choose the service this document will be used for.')
      return
    }
    if (resolvedEntityType !== 'ca_firm' && !assessmentYear && !financialYear) {
      setError('Choose the year this document will be used for.')
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit({
        name: resolvedName,
        category,
        assessmentYear: assessmentYear || undefined,
        financialYear: financialYear || undefined,
        workspaceService: resolvedService || undefined,
        description: description.trim() || undefined,
        file,
        linkedEntityType: resolvedEntityType,
        linkedEntityId: resolvedEntityId,
        parentDocumentId: replaceDocument ? replaceDocument.id : undefined,
        version: replaceDocument ? (replaceDocument.version || 1) + 1 : 1,
      })
      reset()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload document')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => {
          if (!isSubmitting) onClose()
        }}
      />
      <div className="relative bg-white dark:bg-surface-dark rounded-xl shadow-2xl border border-surface-overlay w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-surface-overlay">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              {replaceDocument ? (
                <RefreshCw className="w-5 h-5 text-primary-600" />
              ) : (
                <Upload className="w-5 h-5 text-primary-600" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">
                {replaceDocument
                  ? `Replace Document (v${(replaceDocument.version || 1) + 1})`
                  : 'Upload Document'}
              </h2>
              {replaceDocument && (
                <p className="text-xs text-text-secondary truncate max-w-[16rem]">
                  Replacing: {replaceDocument.name}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-sm btn-square"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!replaceDocument && (
          <fieldset className="p-4 space-y-3" disabled={isSubmitting}>
            <legend className="px-4 font-medium">Who owns this document?</legend>
            <label className="block">
              <input
                type="radio"
                name="document-destination"
                checked={destination === 'vault'}
                onChange={() => {
                  reset()
                  setDestination('vault')
                }}
              />{' '}
              Client-owned Vault document
              <span className="block text-sm text-text-secondary">
                Original client records and portable outputs. Choose the client and upload in their
                Vault.
              </span>
            </label>
            <label className="block">
              <input
                type="radio"
                name="document-destination"
                checked={destination === 'workspace'}
                onChange={() => {
                  reset()
                  setDestination('workspace')
                }}
              />{' '}
              Firm working document
              <span className="block text-sm text-text-secondary">
                Internal workpapers and firm operational records kept in this workspace.
              </span>
            </label>
          </fieldset>
        )}
        {destination === 'vault' && (
          <div className="p-4 space-y-3">
            <p>
              Choose the client in the Vault, then select the file and confirm its sharing details.
            </p>
            {canAccessRoute(user, 'firmVault') || canAccessRoute(user, 'clientVault') ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  onClose()
                  navigate(
                    user?.authScope === 'client_vault'
                      ? '/client-vault?tab=files'
                      : '/firm-vault?tab=files'
                  )
                }}
              >
                Continue to Client Vault
              </button>
            ) : (
              <p role="alert">
                Your account cannot open the Client Vault. Ask your firm administrator for the
                required Vault access.
              </p>
            )}
          </div>
        )}
        {destination === 'workspace' && (
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">File</label>
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-surface-overlay cursor-pointer hover:bg-surface-overlay/40">
                <FileText className="w-4 h-4 text-text-tertiary" />
                <span className="text-sm text-text-secondary truncate">
                  {file ? file.name : 'Choose a document file'}
                </span>
                <input
                  type="file"
                  className="hidden"
                  onChange={e => {
                    const f = e.target.files?.[0]
                    setFile(f)
                    if (f && !name.trim()) setName(f.name)
                  }}
                  disabled={isSubmitting}
                />
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">
                Document Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Form 16 FY2024-25"
                className="input input-bordered w-full"
                disabled={isSubmitting}
              />
            </div>

            {!linkedEntityId && (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                  Associate with Customer
                </label>
                <select
                  value={selectedCustomerId}
                  onChange={e => setSelectedCustomerId(e.target.value)}
                  className="input input-bordered w-full"
                  disabled={isSubmitting}
                  aria-label="Associate with Customer"
                >
                  <option value="">Firm Document (Practice-wide)</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.fullName} ({c.pan})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {customerId && !replaceDocument && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-primary">
                  Assessment
                  <select
                    className="input input-bordered mt-1 w-full"
                    aria-label="Link document to assessment"
                    value={assessmentLink}
                    disabled={isSubmitting || assessmentsLoading}
                    onChange={event => {
                      setAssessmentLink(event.target.value)
                      const selected = assessments.find(
                        item => `${item.type}:${item.id}` === event.target.value
                      )
                      setAssessmentYear(selected?.year ?? '')
                      setFinancialYear('')
                    }}
                  >
                    <option value="">Personal document (customer profile)</option>
                    {assessments.map(item => (
                      <option key={`${item.type}:${item.id}`} value={`${item.type}:${item.id}`}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="text-sm text-text-secondary">
                  Choose the specific assessment when this document supports a return. Identity
                  documents may remain on the customer profile.
                </p>
                {assessmentError && (
                  <p role="alert" className="text-red-600 dark:text-red-400 text-sm">
                    {assessmentError}
                  </p>
                )}
                {canCreateCase && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => {
                      onClose()
                      navigate(
                        `/return-preparation-cases?customerId=${encodeURIComponent(customerId)}`
                      )
                    }}
                  >
                    Add assessment
                  </button>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">Category</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value as DocumentCategory)}
                className="input input-bordered w-full"
                disabled={isSubmitting}
              >
                {categoryOptions.map(c => (
                  <option key={c} value={c}>
                    {labelForCategory(c)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">
                Assessment Year
              </label>
              <select
                value={assessmentYear}
                aria-label="Assessment year"
                onChange={e => {
                  setAssessmentYear(e.target.value)
                  setFinancialYear('')
                }}
                className="input input-bordered w-full"
                disabled={isSubmitting || Boolean(assessmentLink)}
              >
                <option value="">None / Not Applicable</option>
                {ASSESSMENT_YEAR_OPTIONS.map(ay => (
                  <option key={ay} value={ay}>
                    AY {ay}
                  </option>
                ))}
              </select>
            </div>

            {!firmDocument && (
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="document-work-service"
                    className="block text-sm font-medium text-text-primary mb-1.5"
                  >
                    Service
                  </label>
                  <select
                    id="document-work-service"
                    value={
                      assessmentLink.startsWith('assessment_case:') ||
                      replaceDocument?.linkedEntityType === 'assessment_case'
                        ? 'income_tax'
                        : workspaceService
                    }
                    onChange={event =>
                      setWorkspaceService(event.target.value as DocumentWorkspaceService | '')
                    }
                    disabled={
                      isSubmitting ||
                      assessmentLink.startsWith('assessment_case:') ||
                      replaceDocument?.linkedEntityType === 'assessment_case'
                    }
                    className="input input-bordered w-full"
                  >
                    <option value="">Choose a service</option>
                    <option value="income_tax">Income tax</option>
                    <option value="gst">GST</option>
                    <option value="finance">Finance</option>
                  </select>
                </div>
                {!assessmentLink && (
                  <div>
                    <label
                      htmlFor="document-work-year"
                      className="block text-sm font-medium text-text-primary mb-1.5"
                    >
                      Financial year
                    </label>
                    <select
                      id="document-work-year"
                      value={financialYear}
                      onChange={event => {
                        setFinancialYear(event.target.value)
                        setAssessmentYear('')
                      }}
                      disabled={isSubmitting}
                      className="input input-bordered w-full"
                    >
                      <option value="">Choose a financial year</option>
                      {ASSESSMENT_YEAR_OPTIONS.map(ay => {
                        const start = Number(ay.slice(0, 4)) - 1
                        const fy = `${start}-${String((start + 1) % 100).padStart(2, '0')}`
                        return (
                          <option key={fy} value={fy}>
                            {fy}
                          </option>
                        )
                      })}
                    </select>
                    <p className="mt-1 text-xs text-text-secondary">
                      Choose the year of the work that uses this document.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">
                Description
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Optional notes"
                rows={2}
                className="input input-bordered w-full"
                disabled={isSubmitting}
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-ghost"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span className="loading loading-spinner loading-sm" /> Uploading…
                  </>
                ) : (
                  'Upload'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
